import { useState } from 'react';
import { Stack, Text, Textarea, TextInput, Group } from '@mantine/core';
import { Megaphone, Send } from 'lucide-react';
import { Badge, Button, Card } from '../ui';
import { useAnnouncements, useCreateAnnouncement } from '../../hooks/useAnnouncements';
import { toast } from '../../store/useToasts';

/** Cohort announcements: instructors post; members read. */
export function Announcements({ cohortId, canPost }: { cohortId: string; canPost: boolean }) {
  const q = useAnnouncements(cohortId);
  const create = useCreateAnnouncement(cohortId);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const post = () => {
    if (!title.trim() || !body.trim()) { toast.error('Title and message are required'); return; }
    create.mutate({ title: title.trim(), body: body.trim() }, {
      onSuccess: () => { toast.success('Announcement posted'); setTitle(''); setBody(''); },
      onError: (e) => toast.error(e.message || 'Could not post'),
    });
  };

  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

  return (
    <Stack gap="md">
      {canPost && (
        <Card>
          <Stack gap="sm">
            <Group gap="xs"><Megaphone size={16} /><Text fw={600} size="sm">Post an announcement</Text></Group>
            <TextInput placeholder="Title" value={title} onChange={(e) => setTitle(e.currentTarget.value)} maxLength={200} />
            <Textarea placeholder="Message to the cohort…" value={body} onChange={(e) => setBody(e.currentTarget.value)}
              autosize minRows={2} maxRows={6} maxLength={8000} />
            <Group justify="flex-end">
              <Button variant="primary" size="xs" leftIcon={<Send size={12} />} loading={create.isPending} onClick={post}>
                Post
              </Button>
            </Group>
          </Stack>
        </Card>
      )}
      {q.isPending ? (
        <Text size="sm" c="dimmed">Loading announcements…</Text>
      ) : (q.data?.length ?? 0) === 0 ? (
        <Group gap="xs"><Megaphone size={16} /><Text size="sm" c="dimmed">No announcements yet.</Text></Group>
      ) : (
        <Stack gap="sm">
          {q.data!.map((a) => (
            <Card key={a.id}>
              <Stack gap={4}>
                <Group justify="space-between" wrap="nowrap">
                  <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">{a.title}</Text>
                  <Badge variant="info" size="xs">{fmt(a.createdAt)}</Badge>
                </Group>
                <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>{a.body}</Text>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
